import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Bounce, toast } from "react-toastify";
import {
  Activity,
  Calendar,
  Clock,
  Gamepad2,
  Loader2,
  MapPin,
  Medal,
  RefreshCw,
  Sparkles,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getUserPlayedGameSessions } from "../api/userApi";
import LeaderboardSection from "../components/Leaderboard/LeaderboardSection";
import { getResultStatus, getResultStatusClassName, isBestTimeCandidate } from "../lib/gameResults";

const isAutoTeamName = (name) => !name || /^team\s*\d+$/i.test(String(name).trim());

const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const formatDuration = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const totalMilliseconds = Number(value);
  if (!Number.isFinite(totalMilliseconds)) return value;

  const totalSeconds = Math.floor(totalMilliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = totalMilliseconds % 1000;

  return `${minutes}:${seconds.toString().padStart(2, "0")}.${milliseconds
    .toString()
    .padStart(3, "0")}`;
};

const getPlayerResult = (session, userId) => {
  if (!session || !userId) return null;

  const normalizedUserId = String(userId);
  const playerResults = Array.isArray(session.playerResults) ? session.playerResults : [];
  const fallbackResults = Array.isArray(session.results) ? session.results : [];

  return (
    playerResults.find((item) => String(item.userId) === normalizedUserId) ||
    fallbackResults.find((item) => String(item.userId) === normalizedUserId) ||
    null
  );
};

const formatOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : "-";
};

const formatTrackOrLevel = (result) => {
  if (!result) return "-";
  if (result.trackName) return result.trackName;
  if (result.gameLevel !== null && result.gameLevel !== undefined) {
    return `Level ${result.gameLevel}`;
  }

  return "-";
};

const getWinnerUserIds = (session) => {
  if (!session) return new Set();

  const winnerTeamIds = new Set();
  const winnerUserIds = new Set();
  const rankings = Array.isArray(session.Rankings) ? session.Rankings : [];
  const matches = Array.isArray(session.Matches) ? session.Matches : [];
  const teams = Array.isArray(session.GameSessionTeams) ? session.GameSessionTeams : [];

  rankings.forEach((ranking) => {
    if (Number(ranking?.rank) !== 1) return;

    if (ranking?.teamId !== null && ranking?.teamId !== undefined) {
      winnerTeamIds.add(String(ranking.teamId));
    }

    const rankingTeamPlayers = Array.isArray(ranking?.GameSessionTeam?.GameSessionPlayers)
      ? ranking.GameSessionTeam.GameSessionPlayers
      : [];
    rankingTeamPlayers.forEach((player) => {
      if (player?.userId !== null && player?.userId !== undefined) {
        winnerUserIds.add(String(player.userId));
      }
    });
  });

  matches.forEach((match) => {
    if (match?.winnerId !== null && match?.winnerId !== undefined) {
      winnerTeamIds.add(String(match.winnerId));
    }

    const winnerPlayers = Array.isArray(match?.Winner?.GameSessionPlayers)
      ? match.Winner.GameSessionPlayers
      : [];
    winnerPlayers.forEach((player) => {
      if (player?.userId !== null && player?.userId !== undefined) {
        winnerUserIds.add(String(player.userId));
      }
    });
  });

  teams.forEach((team) => {
    if (!winnerTeamIds.has(String(team?.id))) return;

    const teamPlayers = Array.isArray(team?.GameSessionPlayers) ? team.GameSessionPlayers : [];
    teamPlayers.forEach((player) => {
      if (player?.userId !== null && player?.userId !== undefined) {
        winnerUserIds.add(String(player.userId));
      }
    });
  });

  return winnerUserIds;
};

const Home = () => {
const { userData } = useAuth();
const { t } = useTranslation();
const [history, setHistory] = useState(null);
const [isLoading, setIsLoading] = useState(false);

const fetchHistory = useCallback(async () => {
  if (!userData?.id) return;

  setIsLoading(true);
  try {
    const response = await getUserPlayedGameSessions(userData.id);
    setHistory(response);
  } catch (error) {
    console.error("Home history load error:", error);
    toast.error(error.message || "Unable to load your game history.", {
      transition: Bounce,
    });
  } finally {
    setIsLoading(false);
  }
}, [userData?.id]);

useEffect(() => {
  fetchHistory();
}, [fetchHistory]);

const sessions = useMemo(() => {
  return Array.isArray(history?.gameSessions) ? history.gameSessions : [];
}, [history]);

const playerName = history?.user?.full_name || history?.user?.user_name || userData?.full_name || userData?.user_name || "Player";

const stats = useMemo(() => {
  return sessions.reduce(
    (accumulator, session) => {
      const summary = session?.summary || {};
      const playerResult = getPlayerResult(session, userData?.id);
      const scoreValue = Number(playerResult?.score);
      const finishTimeValue = Number(playerResult?.finishTime);
      const pointsValue = Number(playerResult?.pointsGained);

      accumulator.totalSessions += 1;
      accumulator.totalResults += Number(summary.totalResults || (Array.isArray(session?.playerResults) ? session.playerResults.length : 0));
      if (Number.isFinite(scoreValue) && scoreValue > accumulator.bestScore) {
        accumulator.bestScore = scoreValue;
      }

      // A lost run's time is just the time limit (e.g. a Maze timeout), so it
      // never counts as a best time. The game/track is kept so the tile can
      // say which game the best time is from.
      if (isBestTimeCandidate(playerResult) && (accumulator.bestTime === null || finishTimeValue < accumulator.bestTime)) {
        accumulator.bestTime = finishTimeValue;
        accumulator.bestTimeLabel = [session.gameMode?.title, playerResult?.trackName].filter(Boolean).join(" • ");
      }

      if (Number.isFinite(pointsValue)) {
        accumulator.totalPoints += pointsValue;
      }
      return accumulator;
    },
    {
      totalSessions: 0,
      totalResults: 0,
      bestScore: 0,
      bestTime: null,
      bestTimeLabel: "",
      totalPoints: 0,
    }
  );
}, [sessions, userData?.id]);



return (
  <div className="min-h-screen bg-gray-100 text-gray-900">
    <main className="py-6">
      <section className="overflow-hidden rounded-3xl bg-slate-900 text-white shadow-xl">
        <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-400 p-[1px]">
          <div className="rounded-[calc(1.5rem-1px)] bg-slate-900 px-6 py-8 md:px-10">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-orange-200">
                  <Gamepad2 size={14} />
                  Player History
                </div>
                <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{playerName}&apos;s game sessions</h1>
                <p className="mt-3 max-w-2xl text-sm text-slate-300 md:text-base">
                  Track where you played, which game modes you joined, the results you achieved, and where you stand on the leaderboards. This page will later expand with events and tournaments.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link
                  to="/teams"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 px-5 py-3 text-sm font-semibold text-slate-900 transition hover:bg-orange-400"
                >
                  <Users size={18} />
                  {t("mySquads", { defaultValue: "My Squads" })}
                </Link>
                <button
                  type="button"
                  onClick={fetchHistory}
                  disabled={isLoading || !userData?.id}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
                  Refresh history
                </button>
              </div>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                <div className="flex items-center justify-between text-orange-200">
                  <Activity size={20} />
                  <span className="text-xs uppercase tracking-[0.2em]">Sessions</span>
                </div>
                <p className="mt-4 text-3xl font-bold">{history?.totalSessions ?? stats.totalSessions}</p>
                <p className="mt-2 text-sm text-slate-300">Total sessions played</p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                <div className="flex items-center justify-between text-amber-200">
                  <Trophy size={20} />
                  <span className="text-xs uppercase tracking-[0.2em]">Best Score</span>
                </div>
                <p className="mt-4 text-3xl font-bold">{stats.bestScore || 0}</p>
                <p className="mt-2 text-sm text-slate-300">Highest score from your recorded sessions</p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                <div className="flex items-center justify-between text-sky-200">
                  <Clock size={20} />
                  <span className="text-xs uppercase tracking-[0.2em]">Best Time</span>
                </div>
                <p className="mt-4 text-3xl font-bold">{formatDuration(stats.bestTime)}</p>
                <p className="mt-2 text-sm text-slate-300">{stats.bestTimeLabel || "Fastest completed run"}</p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                <div className="flex items-center justify-between text-emerald-200">
                  <Zap size={20} />
                  <span className="text-xs uppercase tracking-[0.2em]">Points</span>
                </div>
                <p className="mt-4 text-3xl font-bold">{stats.totalPoints}</p>
                <p className="mt-2 text-sm text-slate-300">Points gained across your visible results</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {!isLoading && <LeaderboardSection sessions={sessions} userData={userData} />}

      <section className="mt-8 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200 md:p-8">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Recent gameplay</h2>
            <p className="mt-1 text-sm text-gray-500">
              Your personal session history from the game platform.
            </p>
          </div>

          <div className="rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600">
            {sessions.length} session{sessions.length === 1 ? "" : "s"}
          </div>
        </div>

        {isLoading ? (
          <div className="rounded-3xl border border-dashed border-gray-200 p-12 text-center text-gray-500">
            <Loader2 size={28} className="mx-auto mb-3 animate-spin" />
            Loading your game history...
          </div>
        ) : sessions.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 p-12 text-center text-gray-500">
            <Calendar size={28} className="mx-auto mb-3 text-gray-400" />
            No played game sessions were found for your account yet.
          </div>
        ) : (
          <div className="space-y-5">
            {sessions.map((session) => {
              const playerResult = getPlayerResult(session, userData?.id);
              const players = Array.isArray(session.players) ? session.players : [];
              const summary = session.summary || {};
              const hasPlayerResult = Boolean(playerResult);
              const isTimeBased = playerResult?.resultType === "time";
              const performanceValue = !hasPlayerResult
                ? "No result"
                : isTimeBased
                  ? formatDuration(playerResult?.finishTime)
                  : formatOptionalNumber(playerResult?.score);
              const trackOrLevel = formatTrackOrLevel(playerResult);
              const sessionTotalPlayers = summary.totalPlayers ?? session.numberOfPlayers ?? players.length;
              const sessionTotalTeams = summary.totalTeams ?? (Array.isArray(session.GameSessionTeams) ? session.GameSessionTeams.length : 0);
              const sessionTotalResults = summary.totalResults ?? (Array.isArray(session.results) ? session.results.length : 0);
              const winnerUserIds = getWinnerUserIds(session);
              const isTwoPlayersMatch = Number(sessionTotalPlayers) === 2;
              const winnerPlayers = players.filter((player) => winnerUserIds.has(String(player.id)));
              const winnerLabel = winnerPlayers
                .map((player) => player.full_name || player.user_name || "Player")
                .join(" / ");
              const myTeam = Array.isArray(session.playerTeams) ? session.playerTeams[0] : null;
              const myTeamName = myTeam ? myTeam.teamName || `Team${myTeam.id}` : null;
              const myTeamNeedsName = myTeam && isAutoTeamName(myTeam.teamName);
              // Short status such as "won" / "lost" (Maze); JSON payloads from
              // other games are ignored.
              const resultStatus = getResultStatus(playerResult);

              return (
                <article
                  key={session.id}
                  className="overflow-hidden rounded-3xl border border-gray-200 bg-gradient-to-br from-white via-white to-orange-50 shadow-sm"
                >
                  <div className="border-b border-gray-200 px-6 py-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-orange-700">
                            {session.gameSessionType || "Session"}
                          </span>
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                            #{session.id}
                          </span>
                          {resultStatus && (
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] ${getResultStatusClassName(resultStatus)}`}>
                              {resultStatus}
                            </span>
                          )}
                          {myTeam && (
                            <Link
                              to={`/teams/${myTeam.id}`}
                              className={`inline-flex min-h-[32px] items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition ${
                                myTeamNeedsName
                                  ? "bg-[#120239] text-white hover:bg-[#1d0a52]"
                                  : "bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200 hover:bg-indigo-100"
                              }`}
                            >
                              {myTeamNeedsName ? <Sparkles size={13} /> : <Users size={13} />}
                              {myTeamNeedsName
                                ? t("nameTeamNudge", { defaultValue: "{{name}} · Name your team", name: myTeamName })
                                : myTeamName}
                            </Link>
                          )}
                        </div>
                        <h3 className="mt-3 text-2xl font-bold text-gray-900">
                          {session.gameMode?.title || "Game mode unavailable"}
                        </h3>
                        {isTwoPlayersMatch && winnerPlayers.length > 0 && (
                          <p className="mt-2 inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-emerald-700">
                            <Trophy size={14} />
                            Winner: {winnerLabel}
                          </p>
                        )}
                        <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-500">
                          <span className="inline-flex items-center gap-2">
                            <MapPin size={16} />
                            {session.location?.name || "Unknown location"}
                          </span>
                          <span className="inline-flex items-center gap-2">
                            <Gamepad2 size={16} />
                            {session.machine?.type || "Unknown machine"}
                          </span>
                          <span className="inline-flex items-center gap-2">
                            <Calendar size={16} />
                            {formatDateTime(session.createdAt)}
                          </span>
                        </div>
                      </div>

                      <div className="grid min-w-full gap-3 sm:grid-cols-2 lg:min-w-[18rem]">
                        <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
                          <p className="text-xs uppercase tracking-wide text-gray-500">
                            {hasPlayerResult
                              ? (isTimeBased ? "Your finish time" : "Your score")
                              : "Your result"}
                          </p>
                          <p className="mt-2 text-lg font-semibold text-gray-900">{performanceValue}</p>
                          {isTimeBased && (
                            <p className="mt-1 text-sm text-gray-500">Track: {trackOrLevel}</p>
                          )}
                          {!hasPlayerResult && (
                            <p className="mt-1 text-sm text-gray-500">No result was saved for your player in this session.</p>
                          )}
                        </div>
                        <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Session summary</p>
                          <p className="mt-2 text-lg font-semibold text-gray-900">{sessionTotalPlayers} players</p>
                          <p className="mt-1 text-sm text-gray-500">{sessionTotalTeams} teams • {sessionTotalResults} results</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 px-6 py-5 lg:grid-cols-4">
                    <div className="rounded-2xl bg-white p-4 ring-1 ring-gray-100">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <Trophy size={18} className="text-orange-500" />
                        Score
                      </div>
                      <p className="mt-3 text-2xl font-bold text-gray-900">{formatOptionalNumber(playerResult?.score)}</p>
                    </div>

                    <div className="rounded-2xl bg-white p-4 ring-1 ring-gray-100">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <Clock size={18} className="text-sky-500" />
                        Finish Time
                      </div>
                      <p className="mt-3 text-2xl font-bold text-gray-900">{formatDuration(playerResult?.finishTime)}</p>
                    </div>

                    <div className="rounded-2xl bg-white p-4 ring-1 ring-gray-100">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <Zap size={18} className="text-emerald-500" />
                        Points Gained
                      </div>
                      <p className="mt-3 text-2xl font-bold text-gray-900">{formatOptionalNumber(playerResult?.pointsGained)}</p>
                    </div>

                    <div className="rounded-2xl bg-white p-4 ring-1 ring-gray-100">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <Medal size={18} className="text-violet-500" />
                        Track / Level
                      </div>
                      <p className="mt-3 text-lg font-bold text-gray-900">{trackOrLevel}</p>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 bg-white/80 px-6 py-5">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <Users size={18} className="text-slate-500" />
                      Players in this session
                    </div>

                    <div className="mt-4 flex flex-wrap gap-3">
                      {players.length === 0 ? (
                        <span className="text-sm text-gray-500">No player list was returned for this session.</span>
                      ) : (
                        players.map((player) => (
                          <div
                            key={player.id}
                            className={`rounded-2xl px-4 py-3 text-sm shadow-sm ring-1 ${isTwoPlayersMatch && winnerUserIds.has(String(player.id))
                              ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
                              : String(player.id) === String(userData?.id)
                                ? "bg-orange-50 text-orange-800 ring-orange-200"
                                : "bg-gray-50 text-gray-700 ring-gray-200"
                            }`}
                          >
                            <p className="font-semibold">{player.full_name || player.user_name || "Player"}</p>
                            <p className="text-xs opacity-80">@{player.user_name || "unknown"}</p>
                            {isTwoPlayersMatch && winnerUserIds.has(String(player.id)) && (
                              <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide">
                                <Trophy size={12} />
                                Winner
                              </p>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  </div>
);
};

export default Home;
